import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { useApi } from '../hooks/useApi';
import { isViewerHome } from '../utils/access';
import { guestNeedsOnboarding } from '../utils/subscriptionGate';
import {
  getCompletedStepCount,
  getCurrentStepId,
  getOnboardingProgress,
  inicioOnboardingPath,
  isActivationComplete,
  ONBOARDING_STEPS,
} from '../utils/onboarding';

const EMPTY_API_PROGRESS = {
  branding: false,
  client: false,
  sale: false,
  expense: false,
  collection: false,
};

const HIDDEN_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/restablecer-clave',
  '/completar-perfil',
  '/pago/resultado',
  '/terminos',
  '/privacidad',
  '/aceptar-contador',
  '/planes',
  '/funcionalidades',
  '/quienes-somos',
  '/ayuda',
  '/contacto',
  '/movil',
];

function shouldHidePath(pathname) {
  if (pathname === '/inicio' || pathname === '/') return true;
  return HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export default function OnboardingProgressBanner() {
  const { user, token } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const { secureFetch } = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const [apiProgress, setApiProgress] = useState(EMPTY_API_PROGRESS);
  const [unpaidCount, setUnpaidCount] = useState(0);
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!token || !user || isViewerHome(user) || guestNeedsOnboarding(user)) {
      setReady(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await secureFetch('/api/onboarding/status');
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (data?.progress) setApiProgress(data.progress);
          if (data?.counts?.impagas != null) setUnpaidCount(data.counts.impagas);
        }
      } catch {
        /* fallback: asumir pasos pendientes */
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [token, user, secureFetch, location.pathname]);

  const onboardingProgress = useMemo(
    () => getOnboardingProgress({
      branding: apiProgress.branding,
      clientes: apiProgress.client ? 1 : 0,
      facturas: apiProgress.sale ? 1 : 0,
      egresos: apiProgress.expense ? 1 : 0,
      collection: apiProgress.collection,
      pendientes: unpaidCount,
    }),
    [apiProgress, unpaidCount],
  );

  if (!user || !token || !ready) return null;
  if (shouldHidePath(location.pathname)) return null;
  if (isActivationComplete(onboardingProgress)) return null;
  if (dismissed) return null;

  const completed = getCompletedStepCount(onboardingProgress);
  const total = ONBOARDING_STEPS.length;
  const currentId = getCurrentStepId(onboardingProgress);
  const currentStep = ONBOARDING_STEPS.find((s) => s.id === currentId);

  const barClass = modoOscuro
    ? 'bg-[#1F80FF]/20 border-[#1F80FF]/50 text-blue-50'
    : 'bg-[#1F80FF]/12 border-[#1F80FF]/35 text-[#003d8f]';

  return (
    <div className={`relative z-40 border-b px-4 py-2.5 ${barClass}`}>
      <div className="max-w-[85rem] mx-auto flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm">
        <p className="font-medium">
          Primeros pasos: {completed}/{total} completados
          {currentStep ? ` · sigue: ${currentStep.label}` : ''}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(inicioOnboardingPath())}
            className="inline-flex items-center gap-1 rounded-lg bg-[#1F80FF] px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
          >
            Ver checklist
            <FiArrowRight className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className={`px-2 py-1 text-xs font-medium underline-offset-2 hover:underline ${
              modoOscuro ? 'text-blue-200/90' : 'text-[#004AAD]/85'
            }`}
          >
            Ocultar
          </button>
        </div>
      </div>
    </div>
  );
}
